<div class="user-roles-reassignment-view">
  <div class="back-button" (click)="onBackClick()">
    <mat-icon>arrow_back</mat-icon>
    <span>Back to Reassignment Types</span>
  </div>

  <div class="user-roles-reassignment-content">
    <!-- Info Panel -->
    <div class="info-panel">
      <div class="info-content">
        <mat-icon class="info-icon">info</mat-icon>
        <div class="info-text">
          <p>
            Users with primary or secondary management assignments must have
            those assignments removed first before changing their role.
          </p>
        </div>
      </div>
    </div>

    <!-- Step 1: Filter Staff by Role -->
    <div class="step-container">
      <h2 class="step-title">Step 1: Filter Staff by Role</h2>
      <div class="dropdown-container">
        <label class="dropdown-label">Choose a role to filter staff:</label>
        <select
          class="role-dropdown"
          [(ngModel)]="selectedFilterRole"
          (change)="onFilterRoleSelected(selectedFilterRole)"
        >
          <option value="">Choose a role to filter staff…</option>
          <option *ngFor="let role of availableRoles" [ngValue]="role">
            {{ role.roleName }}
          </option>
        </select>
      </div>
    </div>

    <!-- Loading state for staff -->
    <div
      class="step-container loading-state"
      *ngIf="selectedFilterRole && isLoadingStaff"
    >
      <div class="loading-content">
        <app-loading-spinner></app-loading-spinner>
        <p>Loading staff members...</p>
      </div>
    </div>

    <!-- Staff List -->
    <div
      class="step-container"
      *ngIf="selectedFilterRole && userList.length > 0 && !isLoadingStaff"
    >
      <h2 class="step-title">Staff List</h2>
      <div class="staff-list-container">
        <div class="list-header">
          <h3>Available Staff</h3>
          <div class="select-all-container">
            <mat-checkbox
              [checked]="selectAllUsers"
              [indeterminate]="
                selectedUsers.length > 0 &&
                selectedUsers.length < filteredUserList.length
              "
              (change)="toggleSelectAllUsers()"
              [disabled]="filteredUserList.length === 0"
            >
              Select All
            </mat-checkbox>
          </div>
        </div>

        <div class="search-container">
          <mat-icon class="search-icon">search</mat-icon>
          <input
            type="text"
            placeholder="Search staff…"
            [(ngModel)]="userSearchText"
            (input)="onUserSearchChange(userSearchText)"
            class="search-input"
          />
        </div>

        <div class="list-content">
          <div *ngFor="let user of filteredUserList" class="list-item">
            <mat-checkbox
              [checked]="isUserSelected(user)"
              (change)="toggleUserSelection(user)"
              [disabled]="!user || !user.id"
            >
            </mat-checkbox>
            <span class="item-text"
              >{{ user?.firstName }} {{ user?.lastName }}</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state for no staff with role -->
    <div
      class="step-container empty-state"
      *ngIf="selectedFilterRole && userList.length === 0 && !isLoadingStaff"
    >
      <div class="empty-state-content">
        <mat-icon class="empty-icon">people_outline</mat-icon>
        <h3>No staff are currently assigned to this role.</h3>
      </div>
    </div>

    <!-- Step 2: Select New Role -->
    <div class="step-container" *ngIf="selectedUsers.length > 0">
      <h2 class="step-title">Step 2: Select New Role</h2>
      <div class="dropdown-container">
        <label class="dropdown-label">Select new role to assign:</label>
        <select
          class="role-dropdown"
          [(ngModel)]="selectedNewRole"
          (change)="onNewRoleSelected(selectedNewRole)"
        >
          <option value="">Select new role…</option>
          <option *ngFor="let role of availableRoles" [ngValue]="role">
            {{ role.roleName }}
          </option>
        </select>
      </div>
    </div>

    <!-- Assignment Panel -->
    <div
      class="step-container"
      *ngIf="selectedUsers.length > 0 && selectedNewRole"
    >
      <h2 class="step-title">Assign Selected Staff to New Role</h2>
      <div class="transfer-container">
        <div class="selected-staff-list">
          <h3>Selected Staff ({{ selectedUsers.length }})</h3>
          <div class="selected-staff-items">
            <div
              *ngFor="let user of selectedUsers"
              class="selected-staff-item"
            >
              <span>{{ user.firstName }} {{ user.lastName }}</span>
              <button
                class="remove-btn"
                (click)="toggleUserSelection(user)"
                title="Remove from selection"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div class="assignment-info">
          <h3>Assignment Details</h3>
          <div class="role-info">
            <strong>Current Role:</strong>
            {{ selectedFilterRole.roleName }}
          </div>
          <div class="new-role-info">
            <strong>New Role:</strong>
            {{ selectedNewRole.roleName }}
          </div>
          <div class="staff-count">
            <strong>Staff Count:</strong> {{ selectedUsers.length }}
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button
          class="assign-btn"
          (click)="assignRoleToUsers()"
          [disabled]="
            !selectedNewRole || selectedUsers.length === 0 || isProcessing
          "
        >
          <span *ngIf="!isProcessing">
            Move {{ selectedUsers.length }} Staff Member(s) to
            {{ selectedNewRole.roleName }}
          </span>
          <span *ngIf="isProcessing">
            <i class="fas fa-spinner fa-spin"></i> Processing...
          </span>
        </button>
      </div>

      <!-- Progress indicator -->
      <div
        *ngIf="isProcessing"
        class="progress-indicator"
        id="progress-indicator"
      >
        <div class="progress-message">{{ progressMessage }}</div>
        <div class="progress-details">
          <span class="progress-percentage"
            >{{ Math.round(progressPercentage) }}%</span
          >
          <span *ngIf="estimatedTimeRemaining > 0" class="time-remaining">
            Estimated time remaining: {{ estimatedTimeRemaining }}s
          </span>
        </div>
        <div class="progress-bar">
          <div
            class="progress-fill"
            [style.width.%]="progressPercentage"
          ></div>
        </div>
        <div class="progress-warning">
          <mat-icon>warning</mat-icon>
          <span>Please do not navigate away during this operation</span>
        </div>
      </div>
    </div>

    <!-- Validation message -->
    <div
      class="step-container validation-message"
      *ngIf="selectedUsers.length > 0 && !selectedNewRole"
    >
      <div class="validation-content">
        <mat-icon class="validation-icon">warning</mat-icon>
        <span>Select a new role before assigning.</span>
      </div>
    </div>
  </div>
</div>

